import { differenceInMinutes, format } from "date-fns"

import { TooltipContent } from "@/components/ui/tooltip"
import { formatDuration } from "@/lib/routeDelayDisplay"
import type { StopPresentation } from "@/lib/stopPresentation"
import { cn } from "@/lib/utils"
import type { Route, Stop } from "@/types/collections"

interface StopTooltipContentProps {
  stop: Stop
  route: Route
  presentation: StopPresentation
  isPastDate: boolean
  currentTime: Date
}

export default function StopTooltipContent({
  stop,
  route,
  presentation,
  isPastDate,
  currentTime,
}: StopTooltipContentProps) {
  const { settled, onSiteSince, departureUnrecorded, timeUnrecorded, actualDelayMinutes } =
    presentation

  return (
    <TooltipContent
      side="top"
      className="max-w-xs border-palette-jungleGreen200 bg-white p-3 text-palette-jungleGreen900 shadow-lg"
    >
      <div className="space-y-2">
        <div className="font-semibold">{stop.name}</div>
        <div className="text-xs text-palette-jungleGreen500">
          {stop.address}
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-palette-jungleGreen400">Arrival:</span>
            {timeUnrecorded ? (
              <span className="ml-1 text-palette-jungleGreen400">
                Not recorded
              </span>
            ) : stop.actualArrival ? (
              <div className="mt-0.5">
                {isPastDate ? (
                  <>
                    <div className="font-medium text-emerald-600">
                      {format(stop.actualArrival, "HH:mm")}
                    </div>
                    <div className="text-[10px] text-palette-jungleGreen400 line-through">
                      {format(stop.plannedArrival, "HH:mm")} (planned)
                    </div>
                    {actualDelayMinutes !== null &&
                      actualDelayMinutes !== 0 && (
                        <div
                          className={cn(
                            "mt-0.5 text-[10px] font-medium",
                            actualDelayMinutes > 0
                              ? "text-red-600"
                              : "text-green-600"
                          )}
                        >
                          {actualDelayMinutes > 0 ? "+" : ""}
                          {actualDelayMinutes} min
                        </div>
                      )}
                  </>
                ) : (
                  <span className="font-medium text-emerald-600">
                    {format(stop.actualArrival, "HH:mm")}
                  </span>
                )}
              </div>
            ) : stop.estimatedArrival ? (
              <span className="ml-1 font-medium text-blue-600">
                {format(stop.estimatedArrival, "HH:mm")}{" "}
                <span className="text-[10px] text-blue-400">(ETA)</span>
              </span>
            ) : (
              <span className="ml-1 text-palette-jungleGreen900">
                {format(stop.plannedArrival, "HH:mm")}
              </span>
            )}
          </div>
          <div>
            <span className="text-palette-jungleGreen400">Departure:</span>
            {timeUnrecorded || departureUnrecorded ? (
              <span className="ml-1 text-palette-jungleGreen400">
                Not recorded
              </span>
            ) : stop.actualDeparture ? (
              <div className="mt-0.5">
                {isPastDate ? (
                  <>
                    <div className="font-medium text-emerald-600">
                      {format(stop.actualDeparture, "HH:mm")}
                    </div>
                    <div className="text-[10px] text-palette-jungleGreen400 line-through">
                      {format(stop.plannedDeparture, "HH:mm")} (planned)
                    </div>
                  </>
                ) : (
                  <span className="font-medium text-emerald-600">
                    {format(stop.actualDeparture, "HH:mm")}
                  </span>
                )}
              </div>
            ) : onSiteSince ? (
              /* No departure yet; time on site is the known figure. */
              <div className="mt-0.5">
                <div className="font-medium text-palette-jungleGreen900">
                  On site{" "}
                  {formatDuration(
                    Math.max(0, differenceInMinutes(currentTime, onSiteSince))
                  )}
                </div>
                <div className="text-[10px] text-palette-jungleGreen400">
                  planned {format(stop.plannedDeparture, "HH:mm")}
                </div>
              </div>
            ) : stop.estimatedDeparture ? (
              <span className="ml-1 font-medium text-blue-600">
                {format(stop.estimatedDeparture, "HH:mm")}{" "}
                <span className="text-[10px] text-blue-400">(ETA)</span>
              </span>
            ) : (
              /* Labelled so a planned time next to a live ETA isn't read as live. */
              <span className="ml-1 text-palette-jungleGreen900">
                {format(stop.plannedDeparture, "HH:mm")}{" "}
                <span className="text-[10px] text-palette-jungleGreen400">
                  (planned)
                </span>
              </span>
            )}
          </div>
          <div>
            <span className="text-palette-jungleGreen400">Volume:</span>
            <span className="ml-1 text-palette-jungleGreen900">
              {stop.volumeActual ?? stop.volumeEstimated} items
              {stop.volumeActual != null && (
                <span className="ml-1 text-[10px] text-emerald-600">
                  (actual)
                </span>
              )}
            </span>
          </div>
          <div>
            <span className="text-palette-jungleGreen400">Service:</span>
            <span className="ml-1 text-palette-jungleGreen900">
              {stop.serviceTimeMinutes} min
            </span>
          </div>
        </div>
        {!route.isLocked && !settled && !isPastDate && (
          <div className="mt-2 border-t border-palette-jungleGreen100 pt-2 text-xs font-medium text-blue-600">
            💡 Drag to move • Drag edges to resize
          </div>
        )}
        {isPastDate && !route.isLocked && (
          <div className="mt-2 border-t border-palette-jungleGreen100 pt-2 text-xs text-palette-jungleGreen500">
            View-only: Past dates cannot be edited
          </div>
        )}
        {isPastDate &&
          stop.actualArrival &&
          actualDelayMinutes !== null &&
          actualDelayMinutes !== 0 && (
            <div
              className={cn(
                "mt-2 border-t border-palette-jungleGreen100 pt-2 text-xs font-medium",
                actualDelayMinutes > 0 ? "text-red-600" : "text-green-600"
              )}
            >
              {actualDelayMinutes > 0
                ? `⚠️ Arrived ${actualDelayMinutes} minutes late`
                : `✓ Arrived ${Math.abs(actualDelayMinutes)} minutes early`}
            </div>
          )}
        {!isPastDate && stop.delayMinutes != null && stop.delayMinutes !== 0 && (
          <div
            className={cn(
              "text-xs font-medium",
              stop.delayMinutes > 0 ? "text-red-600" : "text-green-600"
            )}
          >
            {stop.delayMinutes > 0
              ? `Running ${formatDuration(stop.delayMinutes)} late`
              : `Running ${formatDuration(Math.abs(stop.delayMinutes))} ahead`}
          </div>
        )}
      </div>
    </TooltipContent>
  )
}
